import React, { useEffect, useRef, useCallback } from 'react';

import {
  Flex,
  Panel,
  Button,
  useNotification,
  LoadingContainer,
  EmptyStatePlaceholder
} from '@ubisend/pulse-components';
import { useQuery } from '@ubisend/pulse-hooks';
import { PermissionFilter } from '@ubisend/pulse-auth';

const Docs = () => {
  const { data, error, isError, isSuccess, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['docs']
  });
  const { showError } = useNotification();
  const formRef = useRef(null);
  const jwtRef = useRef(null);
  const returnToRef = useRef(null);

  // Submit form with given data
  const submitForm = useCallback((ssoData) => {
    if (!ssoData?.url || !ssoData?.jwt || !ssoData?.return_to) {
      showError('Missing SSO data. Please try again later.');
      return;
    }

    const newWindow = window.open('', 'zendeskSSO');
    if (!newWindow) {
      showError('Popup blocked! Please allow popups for this site.');
      return;
    }

    jwtRef.current.value = ssoData.jwt;
    returnToRef.current.value = ssoData.return_to;

    formRef.current.target = 'zendeskSSO';
    formRef.current.action = ssoData.url;
    formRef.current.submit();
  }, [showError]);

  // Auto-submit on first load when data is ready
  useEffect(() => {
    if (isSuccess) {
      submitForm(data);
    }
  }, [isSuccess, data, submitForm]);

  // Button click: refetch and then submit
  const handleViewDocument = useCallback(async () => {
    try {
      const { data: freshData } = await refetch();
      submitForm(freshData);
    } catch {
      showError('Failed to refresh SSO data. Please try again later.');
    }
  }, [refetch, submitForm, showError]);

  const isUnauthorized = error?.response?.status === 403;

  return (
    <Flex middle fat yScreen>
      {isLoading && <LoadingContainer data-testid="spinner-animation" />}
      {isUnauthorized && (
        <p>You are not authorized to view this document. Please log in.</p>
      )}
      {isError && !isUnauthorized && (
        <p>Failed to load SSO URL. Please try again later.</p>
      )}
      {isSuccess && (
        <Panel>
          <Flex col middle>
            <EmptyStatePlaceholder
              type="files"
              heading="View Document"
              text="Please ensure you allow opening new tabs from this browser"
              actions={
                <PermissionFilter can="edit metrics">
                  <Button
                    variant="primary"
                    icon="eye"
                    aria-label="View Document"
                    onClick={handleViewDocument}
                    disabled={isFetching}
                  >
                    {isFetching ? 'Refreshing...' : 'View Document'}
                  </Button>
                </PermissionFilter>
              }
            />
          </Flex>
        </Panel>
      )}

      <form ref={formRef} method="post" style={{ display: 'none' }}>
        <input ref={jwtRef} type="hidden" name="jwt" />
        <input ref={returnToRef} type="hidden" name="return_to" />
      </form>
    </Flex>
  );
};

export default Docs;